import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import TestGraphPage from './TestGraphPage';
import { hydrateEmbedAuthFromSearch, isEmbedMode, clearEmbedAuth } from '../../utils/embedAuth';
import './EmbedTestGraph.css';

/**
 * EmbedTestGraphPage
 *
 * Public, chrome-less wrapper around TestGraphPage used by partner
 * subdomains (e.g. ``dm.cerebralmachines.ai`` iframing this URL under
 * its own brand). The wrapper:
 *
 *   1. Reads ``?embed_token`` (and optional ``?embed_account``) from
 *      the URL and seeds the embed-auth module so every axios call adds
 *      ``X-CM-Embed-Token`` -- no CerebralMachines user login required.
 *   2. Adds a ``cm-embed`` className to <body> for the lifetime of the
 *      page so the global stylesheet can suppress every piece of CM
 *      brand chrome (BuildGraphMenu, test-graph-header, view tabs,
 *      pipeline-settings header) inside the iframe.
 *   3. Renders the regular TestGraphPage -- no fork, no re-implemented
 *      chat. The user gets the exact same engine, just embedded.
 *
 * The route is registered OUTSIDE the ``/user-home`` ProtectedRoute, so
 * the CM AppShell / sidebar / topbar are never mounted on this page.
 */
export default function EmbedTestGraphPage() {
  const { graphId } = useParams();
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const ok = hydrateEmbedAuthFromSearch(location.search);
    if (!ok || !isEmbedMode()) {
      setAuthError(
        'This page can only be loaded with a valid embed token. '
        + 'Open it from the host product (the partner subdomain that '
        + 'embeds the chat) instead of directly.',
      );
      setReady(true);
      return;
    }
    setReady(true);
  }, [location.search]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.body.classList.add('cm-embed');
    document.documentElement.classList.add('cm-embed');
    return () => {
      document.body.classList.remove('cm-embed');
      document.documentElement.classList.remove('cm-embed');
      clearEmbedAuth();
    };
  }, []);

  if (!ready) {
    return (
      <div className="cm-embed-shell cm-embed-shell--loading">
        <div className="cm-embed-spinner" />
      </div>
    );
  }

  if (authError) {
    return (
      <div className="cm-embed-shell cm-embed-shell--error">
        <h2>Chat unavailable</h2>
        <p>{authError}</p>
      </div>
    );
  }

  return (
    <div className="cm-embed-shell" key={graphId}>
      <TestGraphPage />
    </div>
  );
}
